/* eslint-disable no-empty */
import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Menu, X, ChevronRight } from 'lucide-react';
import { useProgram } from './ProgramContext';

// =================================================================
// TOP BAR
// =================================================================
export const TopBar = () => {
  const { logout } = useProgram();

  return (
    <header
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: 'rgba(245, 240, 230, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--g33-ligne)',
      }}
    >
      <div style={{
        maxWidth: 1280, margin: '0 auto', padding: '16px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Link
          to="/gate33-program"
          style={{
            fontFamily: 'var(--g33-font-display)', fontSize: 22,
            fontWeight: 600, letterSpacing: '-0.01em', textDecoration: 'none',
            color: 'var(--g33-noir)',
          }}
        >
          GATE <span style={{ color: 'var(--g33-or)' }}>33</span>
        </Link>

        <nav style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          {[1, 2, 3].map((d) => (
            <Link
              key={d}
              to={`/gate33-program/day/${d}`}
              className="g33-eyebrow"
              style={{ textDecoration: 'none' }}
            >
              Day {d}
            </Link>
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link
            to="/gate33-program/admin/images"
            className="g33-eyebrow"
            data-testid="admin-images-link"
            style={{ textDecoration: 'none' }}
          >
            Images
          </Link>
          <button
            onClick={logout}
            className="g33-btn-ghost"
            data-testid="program-logout"
            style={{ padding: '8px 0' }}
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>
    </header>
  );
};

// =================================================================
// SIDE RAIL
// =================================================================
export const SideRail = () => {
  const { modules } = useProgram();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const days = useMemo(() => [1, 2, 3].map(d => ({
    day: d,
    items: modules.filter(m => m.day === d).sort((a, b) => a.order - b.order),
  })), [modules]);

  if (!modules.length) return null;

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Toggle module list"
        style={{
          position: 'fixed', top: 76, left: 16, zIndex: 45,
          width: 40, height: 40, background: 'var(--g33-cream-soft)',
          border: '1px solid var(--g33-ligne)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {open ? <X size={16} /> : <Menu size={16} />}
      </button>

      {open && (
        <aside
          style={{
            position: 'fixed', top: 65, left: 0, bottom: 0, zIndex: 44,
            width: 320, background: 'var(--g33-cream-soft)',
            borderRight: '1px solid var(--g33-ligne)',
            overflowY: 'auto', padding: '24px 32px 32px 64px',
          }}
        >
          {days.map(({ day, items }) => (
            <div key={day} style={{ marginBottom: 32 }}>
              <div className="g33-eyebrow" style={{ marginBottom: 12 }}>Day {day}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {items.map(m => {
                  const active = pathname.includes(`/module/${m.slug}`);
                  return (
                    <li key={m.slug} style={{ marginBottom: 4 }}>
                      <Link
                        to={`/gate33-program/module/${m.slug}`}
                        onClick={() => setOpen(false)}
                        style={{
                          display: 'flex', alignItems: 'baseline', gap: 12,
                          padding: '8px 0', textDecoration: 'none',
                          color: active ? 'var(--g33-noir)' : 'var(--g33-charcoal)',
                          fontFamily: 'var(--g33-font-body)', fontSize: 14,
                          borderLeft: active ? '2px solid var(--g33-or)' : '2px solid transparent',
                          paddingLeft: 12,
                        }}
                      >
                        <span style={{
                          fontFamily: 'var(--g33-font-display)',
                          color: 'var(--g33-or)', fontSize: 14,
                          minWidth: 24,
                        }}>
                          {String(m.module_number).padStart(2, '0')}
                        </span>
                        <span style={{ flex: 1 }}>{m.title}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>
      )}
    </>
  );
};

// =================================================================
// BOTTOM DOCK (prev / next module nav)
// =================================================================
export const BottomDock = ({ current, all }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 800);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const idx = all.findIndex(m => m.slug === current.slug);
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx < all.length - 1 ? all[idx + 1] : null;

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
      background: 'rgba(245, 240, 230, 0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid var(--g33-ligne)',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto', padding: '14px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 24,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {prev ? (
            <Link to={`/gate33-program/module/${prev.slug}`} style={{
              textDecoration: 'none', color: 'var(--g33-charcoal)',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />
              <div>
                <div className="g33-eyebrow">Previous</div>
                <div style={{ fontSize: 13, marginTop: 2 }}>{prev.title}</div>
              </div>
            </Link>
          ) : <span />}
        </div>

        <div className="g33-eyebrow" style={{ textAlign: 'center' }}>
          Module {String(current.module_number).padStart(2, '0')} of 14
        </div>

        <div style={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
          {next ? (
            <Link to={`/gate33-program/module/${next.slug}`} style={{
              textDecoration: 'none', color: 'var(--g33-charcoal)',
              display: 'inline-flex', alignItems: 'center', gap: 10,
            }}>
              <div>
                <div className="g33-eyebrow">Next</div>
                <div style={{ fontSize: 13, marginTop: 2 }}>{next.title}</div>
              </div>
              <ChevronRight size={14} />
            </Link>
          ) : <span />}
        </div>
      </div>
    </div>
  );
};
